import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import firebase from 'firebase/app';
import 'firebase/firestore';
import TabDescription from './TabDescription';
import { useRegionContext } from '../../region/RegionContext';

const INTENT_COLORS = {
  discovery: '#523384',
  menu_question: '#E65100',
  accessibility_question: '#1976D2',
  venue_info: '#2E7D32',
  general: '#757575',
};

const SOURCE_COLORS = {
  home_longpress: '#523384',
  business_fab: '#B91C1C',
};

const AiAnalyticsTab = () => {
  const { db } = useRegionContext();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30days');
  const [events, setEvents] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const endDate = new Date();
      const startDate = new Date();
      if (dateRange === '7days') startDate.setDate(endDate.getDate() - 7);
      else if (dateRange === '30days') startDate.setDate(endDate.getDate() - 30);
      else if (dateRange === '90days') startDate.setDate(endDate.getDate() - 90);
      else if (dateRange === 'year') startDate.setFullYear(endDate.getFullYear() - 1);

      const snapshot = await db
        .collection('_aiAnalytics')
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startDate))
        .orderBy('timestamp', 'desc')
        .limit(5000)
        .get();

      setEvents(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      console.error('Error loading AI analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Split events by type
  const queryEvents = events.filter(e => !e.type || e.type !== 'summary_generation');
  const summaryEvents = events.filter(e => e.type === 'summary_generation');

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading AI analytics...</Typography>
      </Box>
    );
  }

  // --- Compute query metrics ---
  const totalQueries = queryEvents.length;
  const errorCount = queryEvents.filter(e => e.error).length;
  const errorRate = totalQueries > 0 ? ((errorCount / totalQueries) * 100).toFixed(1) : '0';

  const totalTokensIn = events.reduce((sum, e) => sum + (e.tokensIn || 0), 0);
  const totalTokensOut = events.reduce((sum, e) => sum + (e.tokensOut || 0), 0);
  const avgProcessingMs = totalQueries > 0
    ? Math.round(queryEvents.reduce((sum, e) => sum + (e.processingMs || 0), 0) / totalQueries)
    : 0;

  // Estimated cost: gpt-4o-mini pricing ~$0.15/1M input, $0.60/1M output
  const estimatedCost = ((totalTokensIn * 0.00000015) + (totalTokensOut * 0.0000006)).toFixed(4);

  // Unique users
  const uniqueUsers = new Set(queryEvents.filter(e => e.uid).map(e => e.uid)).size;

  // Summary generation metrics
  const totalSummaries = summaryEvents.length;
  const summaryTokens = summaryEvents.reduce((sum, e) => sum + (e.tokensIn || 0) + (e.tokensOut || 0), 0);

  // --- Queries over time ---
  const queriesByDay = {};
  queryEvents.forEach(e => {
    const ts = e.timestamp?.toDate?.();
    if (!ts) return;
    const key = ts.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
    if (!queriesByDay[key]) queriesByDay[key] = { date: key, queries: 0, errors: 0, summaries: 0 };
    queriesByDay[key].queries += 1;
    if (e.error) queriesByDay[key].errors += 1;
  });
  summaryEvents.forEach(e => {
    const ts = e.timestamp?.toDate?.();
    if (!ts) return;
    const key = ts.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
    if (!queriesByDay[key]) queriesByDay[key] = { date: key, queries: 0, errors: 0, summaries: 0 };
    queriesByDay[key].summaries += 1;
  });
  const queriesOverTime = Object.values(queriesByDay).reverse();

  // --- Intent distribution ---
  const intentCounts = {};
  queryEvents.forEach(e => {
    if (!e.error && e.intent) {
      intentCounts[e.intent] = (intentCounts[e.intent] || 0) + 1;
    }
  });
  const intentData = Object.entries(intentCounts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  // --- Source distribution ---
  const sourceCounts = {};
  queryEvents.forEach(e => {
    if (e.source) {
      sourceCounts[e.source] = (sourceCounts[e.source] || 0) + 1;
    }
  });
  const sourceData = Object.entries(sourceCounts)
    .map(([name, value]) => ({ name: name === 'home_longpress' ? 'Voice Search' : 'Venue FAB', value }));

  // --- Top queried venues (for business_fab) ---
  const venueCounts = {};
  queryEvents.forEach(e => {
    if (e.bizId && e.source === 'business_fab') {
      if (!venueCounts[e.bizId]) venueCounts[e.bizId] = { bizId: e.bizId, count: 0 };
      venueCounts[e.bizId].count += 1;
    }
  });
  const topVenues = Object.values(venueCounts)
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // --- Recent queries ---
  const recentQueries = queryEvents.slice(0, 20);

  // --- Data sources usage ---
  const dataSourceCounts = {};
  queryEvents.forEach(e => {
    if (e.dataSources) {
      e.dataSources.forEach(ds => {
        dataSourceCounts[ds] = (dataSourceCounts[ds] || 0) + 1;
      });
    }
  });
  const dataSourceData = Object.entries(dataSourceCounts)
    .map(([name, value]) => ({ name: name.replace(/_/g, ' '), value }))
    .sort((a, b) => b.value - a.value);

  return (
    <Box>
      <TabDescription>
        AI feature usage analytics from the _aiAnalytics Firestore collection. Tracks every query
        to the AI endpoint including voice search (HomeScreen long-press) and venue FAB
        (BusinessScreen). Monitor query volume, intent patterns, token costs, error rates,
        and data source utilisation.
      </TabDescription>

      {/* Date range filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              label="Date Range"
            >
              <MenuItem value="7days">Last 7 Days</MenuItem>
              <MenuItem value="30days">Last 30 Days</MenuItem>
              <MenuItem value="90days">Last 90 Days</MenuItem>
              <MenuItem value="year">Last Year</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">Total Queries</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#523384' }}>
                {totalQueries}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">Unique Users</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976D2' }}>
                {uniqueUsers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">Error Rate</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: errorCount > 0 ? '#B91C1C' : '#2E7D32' }}>
                {errorRate}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">Avg Response</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {avgProcessingMs < 1000 ? `${avgProcessingMs}ms` : `${(avgProcessingMs / 1000).toFixed(1)}s`}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">Total Tokens</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {(totalTokensIn + totalTokensOut).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">Est. Cost</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#E65100' }}>
                ${estimatedCost}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Summary generation stats */}
      {totalSummaries > 0 && (
        <Card sx={{ mb: 3, backgroundColor: '#F3EDFF' }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#523384', mb: 1 }}>
              AI Summary Generation
            </Typography>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Summaries Generated</Typography>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>{totalSummaries}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Tokens Used</Typography>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>{summaryTokens.toLocaleString()}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Est. Cost</Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#E65100' }}>
                  ${(summaryTokens * 0.0000003).toFixed(4)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Charts row 1: Queries over time + Intent distribution */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Queries Over Time
              </Typography>
              {queriesOverTime.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={queriesOverTime}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="queries" stroke="#523384" strokeWidth={2} name="Queries" />
                    <Line type="monotone" dataKey="summaries" stroke="#2E7D32" strokeWidth={2} name="Summaries" />
                    <Line type="monotone" dataKey="errors" stroke="#B91C1C" strokeWidth={2} name="Errors" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                  No AI queries recorded in this period
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Intent Distribution
              </Typography>
              {intentData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={intentData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, percent }) => `${name.replace('_', ' ')} ${(percent * 100).toFixed(0)}%`}
                    >
                      {intentData.map((entry) => (
                        <Cell key={entry.name} fill={INTENT_COLORS[entry.name] || '#999'} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                  No data
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts row 2: Source breakdown + Data sources used */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Query Source
              </Typography>
              {sourceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={sourceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" name="Queries">
                      {sourceData.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={entry.name === 'Voice Search' ? SOURCE_COLORS.home_longpress : SOURCE_COLORS.business_fab}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                  No data
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Data Sources Utilised
              </Typography>
              {dataSourceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={dataSourceData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={160} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#523384" name="Times Used" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                  No data
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top queried venues */}
      {topVenues.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Top Queried Venues (FAB)
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" aria-label="Top queried venues">
                <TableHead>
                  <TableRow>
                    <TableCell scope="col" sx={{ fontWeight: 600 }}>#</TableCell>
                    <TableCell scope="col" sx={{ fontWeight: 600 }}>Venue ID</TableCell>
                    <TableCell scope="col" sx={{ fontWeight: 600 }} align="right">Queries</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topVenues.map((v, i) => (
                    <TableRow key={v.bizId}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{v.bizId}</TableCell>
                      <TableCell align="right">{v.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Recent queries */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Recent Queries
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label="Recent AI queries">
              <TableHead>
                <TableRow>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Time</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Source</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Intent</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Query</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }} align="right">Tokens</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }} align="right">Time (ms)</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentQueries.map((e) => {
                  const ts = e.timestamp?.toDate?.();
                  return (
                    <TableRow key={e.id}>
                      <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        {ts ? ts.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={e.source === 'home_longpress' ? 'Voice' : 'FAB'}
                          size="small"
                          sx={{
                            backgroundColor: e.source === 'home_longpress' ? '#F3EDFF' : '#FDE8E8',
                            color: e.source === 'home_longpress' ? '#523384' : '#B91C1C',
                            fontWeight: 600,
                            fontSize: 11,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={e.intent ? e.intent.replace('_', ' ') : '—'}
                          size="small"
                          sx={{
                            backgroundColor: INTENT_COLORS[e.intent] ? `${INTENT_COLORS[e.intent]}20` : '#eee',
                            color: INTENT_COLORS[e.intent] || '#666',
                            fontWeight: 500,
                            fontSize: 11,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.query || '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 12 }}>
                        {((e.tokensIn || 0) + (e.tokensOut || 0)).toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 12 }}>
                        {e.processingMs || '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={e.error ? 'Error' : 'OK'}
                          size="small"
                          color={e.error ? 'error' : 'success'}
                          variant="outlined"
                          sx={{ fontSize: 11 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AiAnalyticsTab;
